import { LayoutImportConverterLocationFragment } from '@warebee/frontend/data-access-api-graphql';
import { LocationPatch } from '@warebee/shared/data-access-layout-import-converter';
import _ from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tag } from 'react-konva';
import { Button } from '../../../../components/actions/Button';
import InboxZero from '../../../../components/InboxZero';
import InputNumber from '../../../../components/inputs/InputNumber';
import { InputValue } from '../../../../components/inputs/InputValue';
import { Container } from '../../../../components/layout/ContainerFlex';
import { ActionBar } from '../../../../components/nav/ActionBar';
import useApplyLocationPatches from '../hooks/useApplyLocationPatch';

type LocationPatchEditorProps = {
  sample: LayoutImportConverterLocationFragment;
  patch: MultiLocationPatch;
  title: string;
  propertyName: keyof MultiLocationPatch;
  onChange: (patch: MultiLocationPatch) => void;
};

const LocationPatchEditor: React.FC<LocationPatchEditorProps> = props => {
  const path = props.propertyName;
  const value = props.patch?.[path];
  const sampleValue = props.sample?.[path];
  return (
    <>
      {typeof sampleValue === 'number' && (
        <InputNumber
          title={props.title}
          ariaLabel={`${path}`}
          value={value as number}
          onChange={v => props.onChange({ ...props.patch, [path]: v })}
        />
      )}
      {typeof sampleValue === 'string' && (
        <InputValue
          title={props.title}
          value={value ?? ''}
          onChange={v =>
            _.isEmpty(v)
              ? props.onChange(_.omit(props.patch, path))
              : props.onChange({ ...props.patch, [path]: v })
          }
          // isDebouncedChange={true}
          // onEndEdit={v => props.onChange({ ...props.patch, [path]: v })}
        />
      )}
      {typeof sampleValue === 'boolean' && (
        <InputValue
          title={props.title}
          value={value ?? ''}
          onChange={v =>
            _.isEmpty(v)
              ? props.onChange(_.omit(props.patch, path))
              : props.onChange({ ...props.patch, [path]: v })
          }
          // isDebouncedChange={true}
          // onEndEdit={v => props.onChange({ ...props.patch, [path]: v })}
        />

        //   <label className="flex">
        //   <div className="flex w-full text-xs uppercase"> {props.title}</div>
        //   <InputCheckbox
        //     label={props.title}
        //     isSelected={Boolean(value)}
        //     onChange={v => props.onChange({ ...props.patch, [path]: v })}
        //   />
        // </label>
      )}
    </>
  );
};

type MultiLocationPatch = Omit<LocationPatch, 'locationId' | 'locationId'>;

export type ConverterUpdateLocationsTableActionProps = {
  selectedLocations: LayoutImportConverterLocationFragment[];
};

const ConverterUpdateLocationsTableAction: React.FC<
  ConverterUpdateLocationsTableActionProps
> = props => {
  const { t } = useTranslation('designer');
  const [applyPatches] = useApplyLocationPatches();
  const [multiPatch, setMultiPatch] = useState<MultiLocationPatch>({});

  const sample = _.head(props.selectedLocations);
  function updateLocations() {
    applyPatches({
      patches: _.map(props.selectedLocations, l => ({
        ...multiPatch,
        locationKey: l.locationKey,
      })),
    });
  }

  const selectedLocationsCount = _.size(props.selectedLocations);

  return (
    <Container
      fullWidth={false}
      col
      overflow
      className="bg-menu/30 border-menu relative min-h-24 min-w-[16rem] border-l"
    >
      <ActionBar sticky stickyTop className="text-xxs gap-1 p-2 py-3 uppercase">
        <Button
          label={t('Batch Update:', {
            selectedBaysCount: selectedLocationsCount,
          })}
          full
          buttonType="primary"
          buttonSize={'xs'}
          className="rounded"
          isDisabled={selectedLocationsCount === 0}
          onPress={updateLocations}
        >
          <Tag className="text-menu-active-text bg-transparent px-0.5 text-sm">
            {selectedLocationsCount}
          </Tag>
        </Button>
        {/* <Button
          label={t('Delete:', {
            selectedBaysCount: selectedLocationsCount,
          })}
          full
          className="rounded"
          buttonType="delete"
          buttonSize={'xs'}
          isDisabled={selectedLocationsCount === 0}
          onPress={removeSpacers}
        >
          <Tag className="text-menu-active-text bg-transparent px-0.5 text-sm">
            {selectedLocationsCount}
          </Tag>
        </Button> */}
      </ActionBar>
      <div className="flex flex-1 flex-col justify-items-start gap-6 p-4">
        {selectedLocationsCount === 0 && (
          <InboxZero message={t`No locations selected`} />
        )}
        <LocationPatchEditor
          patch={multiPatch}
          sample={sample}
          propertyName={'locationBay'}
          title={t`Bay`}
          onChange={p => setMultiPatch(p)}
        />
        <LocationPatchEditor
          patch={multiPatch}
          sample={sample}
          propertyName={'baySide'}
          title={t`Side`}
          onChange={p => setMultiPatch(p)}
        />
        <LocationPatchEditor
          patch={multiPatch}
          sample={sample}
          propertyName={'locationAisle'}
          title={t`Aisle`}
          onChange={p => setMultiPatch(p)}
        />
        <LocationPatchEditor
          patch={multiPatch}
          sample={sample}
          propertyName={'warehouseArea'}
          title={t`Warehouse Area`}
          onChange={p => setMultiPatch(p)}
        />
        <LocationPatchEditor
          patch={multiPatch}
          sample={sample}
          propertyName={'locationStatus'}
          title={t`Status`}
          onChange={p => setMultiPatch(p)}
        />
        <LocationPatchEditor
          patch={multiPatch}
          sample={sample}
          propertyName={'locationLevel'}
          title={t`Level`}
          onChange={p => setMultiPatch(p)}
        />
        <LocationPatchEditor
          patch={multiPatch}
          sample={sample}
          propertyName={'locationLength'}
          title={t`Length`}
          onChange={p => setMultiPatch(p)}
        />
        <LocationPatchEditor
          patch={multiPatch}
          sample={sample}
          propertyName={'locationWidth'}
          title={t`Width`}
          onChange={p => setMultiPatch(p)}
        />
        <LocationPatchEditor
          patch={multiPatch}
          sample={sample}
          propertyName={'locationHeight'}
          title={t`Height`}
          onChange={p => setMultiPatch(p)}
        />
        <LocationPatchEditor
          patch={multiPatch}
          sample={sample}
          propertyName={'locationWeight'}
          title={t`Weight`}
          onChange={p => setMultiPatch(p)}
        />
        <LocationPatchEditor
          patch={multiPatch}
          sample={sample}
          propertyName={'locationDepthPosition'}
          title={t`Depth Position`}
          onChange={p => setMultiPatch(p)}
        />
        <LocationPatchEditor
          patch={multiPatch}
          sample={sample}
          propertyName={'locationUsageType'}
          title={t`Usage Type`}
          onChange={p => setMultiPatch(p)}
        />
        <LocationPatchEditor
          patch={multiPatch}
          sample={sample}
          propertyName={'locationBayPosition'}
          title={t`Bay Position`}
          onChange={p => setMultiPatch(p)}
        />
        <LocationPatchEditor
          patch={multiPatch}
          sample={sample}
          propertyName={'locationRackingType'}
          title={t`Racking Type`}
          onChange={p => setMultiPatch(p)}
        />
        <LocationPatchEditor
          patch={multiPatch}
          sample={sample}
          propertyName={'locationOrder'}
          title={t`Order`}
          onChange={p => setMultiPatch(p)}
        />
      </div>
    </Container>
  );
};
export default ConverterUpdateLocationsTableAction;
